/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useSyncExternalStore
} from 'react';
import type { ReactNode } from 'react';
import {
  CommunicationType,
  Country,
  PhoneType,
  Status
} from '@grc/ui-shared/lib/components/models/enums/enums';
import { ILocationDetails } from '../models/location-details.interface';
import { ICurrencyDetails } from '../models/currency-details.interface';
import { IDamagesClaims } from '../models/damages-claims.interface';
import { IUnitsofMeasure } from '../models/units-of-measure.interface';
import { ITransportationModes } from '../models/transportation-mode.interface';
import {
  IEquipmentTypes,
  IServiceLevels,
  IShipmentTypes
} from '../models/mode-and-equipment.interface';
import { IReferences } from '../models/references-and-documents.interface';

export function createSelectorProxy(): {
  proxy: Record<string, any>;
  getPath: () => string[];
} {
  const lastPath: string[] = [];

  let proxy: Record<string, any>; // <-- Declare before initializing

  proxy = new Proxy(
    {},
    {
      get(_, key: string) {
        lastPath.push(key);
        return proxy;
      }
    }
  );

  return {
    proxy,
    getPath: () => [...lastPath]
  };
}

function getNestedValue<T extends Record<string, any>>(
  obj: T,
  path: string[]
): any {
  return path.reduce((acc, key) => acc?.[key], obj);
}

function setNestedValue<T extends Record<string, any>>(
  obj: T,
  path: string[],
  value: any,
  options?: { forceClone?: boolean }
): T {
  const newObj = { ...obj };
  let temp: any = newObj;

  path.forEach((key, index) => {
    if (index === path.length - 1) {
      if (typeof value === 'function') {
        temp[key] = value(temp[key]);
      } else if (options?.forceClone) {
        temp[key] = structuredClone(value); // full deep clone
      } else if (
        typeof value === 'object' &&
        value !== null &&
        !Array.isArray(value)
      ) {
        temp[key] = { ...temp[key], ...value }; // shallow merge
      } else {
        temp[key] = value;
      }
    } else {
      temp[key] = { ...temp[key] };
      temp = temp[key];
    }
  });

  return newObj;
}

export function createStore<Store extends Record<string, any>>(
  initialState: Store
) {
  function useStoreData(defaultState?: Store) {
    const store = useRef<Store>(
      structuredClone({ ...initialState, ...defaultState })
    );

    const subscribers = useRef(new Set<() => void>());

    const get = useCallback(() => store.current, []);
    const set = useCallback(
      (value: Partial<Store> | ((prev: Store) => Partial<Store>)) => {
        const newState =
          typeof value === 'function' ? value(store.current) : value;
        store.current = { ...store.current, ...newState };
        subscribers.current.forEach(cb => cb());
      },
      []
    );

    const subscribe = useCallback((cb: () => void) => {
      subscribers.current.add(cb);
      return () => subscribers.current.delete(cb);
    }, []);

    return { get, set, subscribe };
  }

  const StoreContext = createContext<ReturnType<typeof useStoreData> | null>(
    null
  );

  function Provider({
    children,
    initialState
  }: {
    children: ReactNode;
    initialState?: Partial<Store>;
  }) {
    const storeData = useStoreData(initialState as Store);
    return (
      <StoreContext.Provider value={storeData}>
        {children}
      </StoreContext.Provider>
    );
  }

  function useStore<SelectorOutput>(
    selector: (store: Store) => SelectorOutput
  ): [
    SelectorOutput,
    (
      value:
        | Partial<SelectorOutput>
        | ((prev: SelectorOutput) => Partial<SelectorOutput>),
      options?: { forceClone?: boolean }
    ) => void
  ] {
    const store = useContext(StoreContext);
    if (!store) throw new Error('Store not found');

    const trackedPath = useMemo(() => {
      const { proxy, getPath } = createSelectorProxy();
      selector(proxy as Store);
      return getPath();
    }, [selector.toString()]);

    const state = useSyncExternalStore(store.subscribe, () =>
      getNestedValue(store.get(), trackedPath)
    );

    const setState = useCallback(
      (
        value:
          | Partial<SelectorOutput>
          | ((prev: SelectorOutput) => Partial<SelectorOutput>),
        options?: { forceClone?: boolean }
      ) => {
        store.set(prev => setNestedValue(prev, trackedPath, value, options));
      },
      [store, trackedPath]
    );

    return [state, setState];
  }

  function useSetStore() {
    const store = useContext(StoreContext);
    if (!store) throw new Error('Store not found');
    return store.set;
  }

  function useSetStorePath<SelectorOutput>(
    selector: (store: Store) => SelectorOutput
  ) {
    const store = useContext(StoreContext);
    if (!store) throw new Error('Store not found');

    const trackedPath = useMemo(() => {
      const { proxy, getPath } = createSelectorProxy();
      selector(proxy as Store);
      return getPath();
    }, [selector.toString()]);

    return useCallback(
      (
        value:
          | Partial<SelectorOutput>
          | ((prev: SelectorOutput) => Partial<SelectorOutput>),
        options?: { forceClone?: boolean }
      ) => {
        store.set(prev => setNestedValue(prev, trackedPath, value, options));
      },
      [store, trackedPath]
    );
  }

  return {
    Provider,
    useStore,
    useSetStore,
    useSetStorePath
  };
}

export const initilaModuleState = {
  name: '',
  remoteName: '',
  remoteUrl: '',
  moduleName: '',
  subModules: []
};

// Then define `initialStoreState` which uses `initilaModuleState`
export interface IStore {
  tabTitle: string;
  selectedLanguage: string;
  module: typeof initilaModuleState;
  tenantDetails: any;
  selectedTheme: string;
  userPermissions: any;
  useDetails: any;
}

export const initialStoreState: IStore = {
  tabTitle: '',
  selectedLanguage: '',
  module: initilaModuleState,
  tenantDetails: '',
  selectedTheme: '',
  userPermissions: '',
  useDetails: ''
};

export const initialCarrierDetailsState: ILocationDetails = {
  id: '',
  tenantId: '',
  displayId: '',
  name: '',
  alias: '',
  generalEmailAddress: '',
  generalPhoneNumber: {
    phoneType: CommunicationType.Mobile,
    phoneNumber: '',
    displayOrder: 0
  },
  address: {
    streetAddress: [''],
    city: '',
    provinceState: '',
    postalZipCode: '',
    country: Country.UnitedStates
  },
  status: Status.Active,
  contacts: [],
  logoUrl: ''
};

export const initialCurrencyDetailsState: ICurrencyDetails = {
  id: '',
  country: '',
  isoCode: '',
  isActive: true,
  countryFlag: '',
  currencyName: '',
  currencySymbol: '',
  currencySymbolOnRight: false
};

export const initialUnitOfMeasureValues: IUnitsofMeasure = {
  id: '',
  unit: '',
  category: '',
  symbol: '',
  status: Status.Active,
  uomConversion: '',
  isDefault: true,
  isActive: true
};

export const initialTransportationModesValues: ITransportationModes = {
  id: '',
  displayName: '',
  ediCodes: '',
  tenderResponseTime: '',
  isActive: true
};

export const initialShipmentTypesValues: IShipmentTypes = {
  id: '',
  internalId: '',
  shipmentTypeName: '',
  isDefault: true,
  isActive: true
};

export const initialServiceLevelValues: IServiceLevels = {
  id: '',
  serviceChargeName: '',
  isStandardService: true,
  description: '',
  isDefault: true,
  isActive: true
};

export const initialEquipmentValues: IEquipmentTypes = {
  id: '',
  equipmentTypeName: '',
  equipmentGroup: '',
  isActive: true
};

export const initialReferencesValues: IReferences = {
  id: '',
  name: '',
  isActive: true
};

// damages and claims initial values

export const initialDamagesClaimsValues: IDamagesClaims = {
  carrierId: '',
  shipmentId: '',
  shipmentNumber: '',
  nplClaim: '',
  origin: initialCarrierDetailsState,
  destination: initialCarrierDetailsState,
  claiminTheAmount: '',
  dateFiled: undefined,
  nplTrack: '',
  claimantsClaim: '',
  billofLading: '',
  claimHereByFiledFor: {
    isShortage: false,
    isDamage: false,
    isOverage: false,
    isOther: false
  },
  carrier: initialCarrierDetailsState, // optional
  pickupReadyDate: undefined,
  pickupCloseDate: undefined,
  deliveryReadyDate: undefined,
  deliveryCloseDate: undefined,
  driverDetails: {
    driverName: '',
    generalPhoneNumber: {
      phoneType: PhoneType.Home,
      phoneNumber: '',
      displayOrder: 0
    }
  }, // optional
  detailedStatement: '',
  totalAmountClaimed: '',
  claimValueBasedOn: {
    isFullInvoiceValue: '',
    isRepair: '',
    isAllowance: ''
  },
  forgoingStatement: {
    claimantsRep: '',
    claimantsSignature: '',
    date: undefined,
    telephone: '',
    fax: '',
    email: '',
    ptiSales: '',
    consultant: '',
    isOriginalBillOfLading: false,
    isOriginalPaidFreightBill: false,
    isCarriersInspectionReportForm: false,
    isPhotographsDocumentingDamage: false,
    isOriginalInvoiceorCertifiedCopy: false,
    isSignedCopyOfTheDeliveryReceipt: false
  }
};
